import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';
import ArticleBody from '@/components/help/ArticleBody';
import GuideCard from '@/components/guides/GuideCard';
import { CATEGORY_ICONS } from '@/components/guides/categoryIcons';
import GuideChart from '@/components/guides/GuideChart';
import { guideCard, guideMeta } from '@/content/guides/catalog';
import { bodySegments, formatGuideDate, guidePath, readMinutes } from '@/content/guides/format';
import { loadGuideBody, loadedGuideBody } from '@/content/guides/bodies/registry';

/**
 * A guide from src/content/guides (/guides/:slug), in the speed benchmark's
 * layout. The header comes from the catalog at once; the body arrives with
 * its own chunk (already there when pre-rendered).
 */
export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const meta = guideMeta(slug);
  const [body, setBody] = useState(() => loadedGuideBody(slug));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    let live = true;
    setBody(loadedGuideBody(slug));
    loadGuideBody(slug).then((b) => { if (live) setBody(b); }).catch(() => {});
    return () => { live = false; };
  }, [slug]);

  if (!meta) return <Navigate to="/guides" replace />;
  const isResearch = meta.category === 'Research';

  return (
    <>
      <PageSeo path={guidePath(slug)} />

      <div className="min-h-screen bg-black text-white">
        <div className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/guides" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" />
              Guides
            </Link>
            <button
              onClick={() => navigate('/search')}
              className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
            >
              Search Flights
            </button>
          </div>
        </div>

        <article className="pt-28 pb-20 px-6">
          <div className="max-w-3xl mx-auto">
            <nav aria-label="Breadcrumb" className="text-sm text-white/45 mb-6">
              <Link to="/" className="hover:text-white/70">Home</Link>
              <span className="mx-2">›</span>
              <Link to="/guides" className="hover:text-white/70">Guides</Link>
            </nav>

            <p className="text-xs uppercase tracking-[0.16em] text-white/55 mb-4">{meta.label || meta.category}</p>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-tight mb-5">{meta.title}</h1>
            <p className="text-lg md:text-xl text-white/75 leading-relaxed mb-4">{meta.dek}</p>
            <p className="text-sm text-white/45 mb-12">
              By {meta.author} · Updated {formatGuideDate(meta.updated)}
              {body && <> · {readMinutes(body)} min read</>}
            </p>

            {!body && <div aria-busy="true" className="h-96" />}

            {body?.keyTakeaways?.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-semibold mb-5">{isResearch ? 'Key findings' : 'Key takeaways'}</h2>
                <ul className="space-y-3">
                  {body.keyTakeaways.map((t) => (
                    <li key={t} className="flex gap-3 text-white/80 leading-relaxed">
                      <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-white/50 shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {body && bodySegments(body.body).map((s, i) => (s.type === 'chart'
              ? body.charts?.[s.id] && <GuideChart key={i} chart={body.charts[s.id]} />
              : <ArticleBody key={i} blocks={s.blocks} />))}

            {body?.methodology?.length > 0 && (
              <section className="mt-14 mb-14">
                <h2 className="text-2xl font-semibold mb-4">How we did this research</h2>
                {body.methodology.map((p) => <p key={p} className="text-[15px] text-white/60 leading-relaxed mb-3">{p}</p>)}
              </section>
            )}

            {meta.faqs?.length > 0 && (
              <section className="mt-14 mb-14">
                <h2 className="text-2xl font-semibold mb-6">Frequently asked questions</h2>
                <div className="divide-y divide-white/[0.08] border-y border-white/[0.08]">
                  {meta.faqs.map((f) => (
                    <div key={f.q} className="py-5">
                      <h3 className="text-lg font-semibold mb-2">{f.q}</h3>
                      <p className="text-white/75 leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {body?.sources?.length > 0 && (
              <section className="mb-14">
                <h2 className="text-lg font-semibold mb-3">Sources</h2>
                <ul className="space-y-1.5 text-sm text-white/55">
                  {body.sources.map((s) => (
                    <li key={s.url}>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-white/80 underline decoration-white/20 underline-offset-[3px]">{s.label}</a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-xl font-semibold mb-1">Search flights on Defera</p>
                <p className="text-white/60">Book now and pay over time, with no credit check and no interest.</p>
              </div>
              <button
                onClick={() => navigate('/search')}
                className="inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search flights <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {meta.related?.length > 0 && (
            <section className="max-w-6xl mx-auto mt-16">
              <h2 className="text-2xl font-semibold mb-6">Keep reading</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {meta.related.map(guideCard).filter(Boolean).map((c) => (
                  <GuideCard key={c.to} {...c} icon={CATEGORY_ICONS[c.section]} />
                ))}
              </div>
            </section>
          )}
        </article>

        <Footer />
      </div>
    </>
  );
}
